import React, { useState } from 'react';
import { Shield, Database, Lock, FileCheck, ChevronDown } from 'lucide-react';
import AnimatedGradientText from './AnimatedGradientText';
import { useLang } from './LanguageProvider';
import translations from '../translations';

const secIcons = [Shield, Database, Lock, FileCheck];
const secColors = ['#10b981', '#06b6d4', '#8b5cf6', '#3b82f6'];

export default function Security() {
  const [openIndex, setOpenIndex] = useState(null);
  const { lang } = useLang();
  const t = translations[lang].security;

  return (
    <section data-testid="security-section" id="security" className="py-20 sm:py-24 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(800px at 50% 30%, rgba(16, 185, 129, 0.07), transparent 55%)' }} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-emerald-500/10 bg-emerald-50 border dark:border-emerald-500/20 border-emerald-200 mb-5">
            <Shield className="w-3 h-3 text-emerald-500" />
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-500">{t.badge}</span>
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4"><AnimatedGradientText>{t.title}</AnimatedGradientText></h2>
          <p className="text-base dark:text-white/50 text-slate-500 max-w-lg mx-auto">{t.desc}</p>
        </div>

        <div className="flex justify-center mb-14">
          <div className="relative w-40 h-40">
            {/* Sonar pulse rings — expanding outward */}
            <div className="absolute inset-0 rounded-full border border-emerald-500/30 security-sonar" />
            <div className="absolute inset-0 rounded-full border border-emerald-500/20 security-sonar-delayed" />
            {/* Structural rings with pulsing wave effect */}
            <div className="absolute inset-0 rounded-full border-[1.5px] border-emerald-500/35 security-ring-1" />
            <div className="absolute inset-3 rounded-full border border-emerald-500/25 security-ring-2" />
            <div className="absolute inset-6 rounded-full border border-emerald-500/15 security-ring-3" />
            {/* Center shield icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.15)]">
                <Shield className="w-8 h-8 text-emerald-400" />
              </div>
            </div>
            {/* Background glow */}
            <div className="absolute inset-0 rounded-full blur-3xl bg-emerald-500/8" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {t.items.map((item, i) => {
            const Icon = secIcons[i];
            const color = secColors[i];
            const isOpen = openIndex === i;
            return (
              <div key={i} data-testid={`security-item-${i}`} className="rounded-2xl overflow-hidden cursor-pointer group glow-card" style={{ '--glow-color': `${color}40` }} onClick={() => setOpenIndex(isOpen ? null : i)}>
                <div className="p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all group-hover:scale-110" style={{ background: `${color}1f`, border: `1px solid ${color}40` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold dark:text-white text-slate-900">{item.title}</h3>
                      <ChevronDown className={`w-4 h-4 dark:text-white/30 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                    {isOpen && <p className="mt-3 text-sm dark:text-white/50 text-slate-500 leading-relaxed">{item.desc}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2.5 px-5 py-3 rounded-2xl" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs dark:text-white/50 text-slate-500 font-medium">{t.trust}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
